import { ExpandedRowVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

/** 行から引ける勤務。shiftTruckId は選択の範囲、shiftId は transfer に渡す値 */
export type StopShiftIds = {
  /** = delivery.id。左リストの車両グループと 1 対 1 */
  shiftTruckId: number;
  /** 自動配車が要求する shift.id。1 つの shift に複数の shift_truck がぶら下がりうる */
  shiftId: number;
};

/** 選択できる停車地 1 件ぶんの解決結果 */
export type StopSelectionEntry = StopShiftIds & {
  rowId: string;
  /** この地点を選んだときに動く legRun。集約カード由来 */
  legRunIds: number[];
};

export type StopSelectionIndex = ReadonlyMap<string, StopSelectionEntry>;

/** 一括割付の移動元。動かす legRun は選択全体から集めるのでここには持たない */
export type StopSelectionSource = StopShiftIds & { rowId: string };

type BuildParams = {
  /** 絞り込み前の可視行。隠れているだけの地点を選択から落とさないため */
  rowGroups: readonly VehicleRowGroup[];
  expandedRowVMByRowId: ReadonlyMap<string, ExpandedRowVM>;
  resolveShiftIds: (rowId: string) => StopShiftIds | null;
};

/**
 * stopId → 選択に必要な情報。
 *
 * Why stopId をキーにする: 同じ集約カードを指す停車地行が複数ありうる
 * （buildExpandedRowVM.ts:14 の cardIndex コメント「再訪は同じ値になりうる」）ため
 * card.id では再訪した 2 行を区別できず、さらに積地カードの id は勤務をまたいで衝突する
 * （logposeTech/log_web_issues#320）。stopId は operation id 列から作られる
 * （buildOperationPlaces.ts:221）ので全行を通して一意。
 *
 * Why fail-closed: 選べても必ず失敗する地点を作らない（buildMoveTargets.ts:23 と同じ規則）。
 * index に入らない地点はクリックしても選択されず、詳細だけが従来どおり動く。
 */
export function buildStopSelectionIndex({
  rowGroups,
  expandedRowVMByRowId,
  resolveShiftIds,
}: BuildParams): StopSelectionIndex {
  const index = new Map<string, StopSelectionEntry>();

  rowGroups.forEach((rowGroup) => {
    rowGroup.rows.forEach((row) => {
      const vm = expandedRowVMByRowId.get(row.rowId);
      if (!vm) return;
      const shiftIds = resolveShiftIds(row.rowId);
      if (!shiftIds) return;

      vm.stops.forEach((stop) => {
        // 集約カードを引けない停車地行は今も onClick を渡していない
        // （ExpandedRowPanel.tsx:299）。選択の対象にもしない
        const legRunIds = row.cards[stop.cardIndex]?.legRunIds;
        if (!legRunIds || legRunIds.length === 0) return;

        index.set(stop.stopId, {
          rowId: row.rowId,
          shiftTruckId: shiftIds.shiftTruckId,
          shiftId: shiftIds.shiftId,
          legRunIds,
        });
      });
    });
  });

  return index;
}

/**
 * 選択中の停車地が属する shift_truck。空か、どれも index に無ければ null。
 *
 * prev に index から消えた stopId が残っていることがある（prune が走るまでの間）ので、
 * 引けるものだけを見る。
 */
function currentScope(prev: ReadonlySet<string>, index: StopSelectionIndex): number | null {
  for (const stopId of prev) {
    const entry = index.get(stopId);
    if (entry) return entry.shiftTruckId;
  }
  return null;
}

/**
 * 停車地の選択トグル。
 *
 * Why 範囲外を「無効」にせず選び直しにする: 押しても何も起きない行を作らないため
 * （logposeTech/log_web_issues#325）。範囲は shift ではなく shift_truck で、理由は
 * 設計書「選択の範囲は shift ではなく shift_truck」を参照。
 *
 * Why index に無い stopId で prev をそのまま返す: 選択できない地点をクリックしただけで
 * 既存の選択が消えると、詳細を覗いたつもりが選択を失う。同一参照で返して下流の memo も守る。
 */
export function toggleStopSelection(
  prev: ReadonlySet<string>,
  stopId: string,
  index: StopSelectionIndex,
): Set<string> {
  const entry = index.get(stopId);
  if (!entry) return prev as Set<string>;

  const scope = currentScope(prev, index);
  if (scope != null && scope !== entry.shiftTruckId) return new Set([stopId]);

  const next = new Set(prev);
  if (next.has(stopId)) next.delete(stopId);
  else next.add(stopId);
  return next;
}

/**
 * 再取得で消えた停車地を選択から外し、範囲が割れていたら選択ごと捨てる。
 *
 * Why 割れたら空にする: toggle 時だけ範囲を揃えても、再取得で選択済み停車地の所属が
 * 変われば不変条件は破れる。割れたまま一括割付を実行すると transfer_from_shift_id が
 * 単数のため移動元の片方が組み直されないまま飛ぶので、fail-closed で捨てる。
 *
 * Why 不変なら同一参照: 参照が変わると下流の memo が無駄に破れる
 * （Map/pruneSelection.ts と同じ流儀）。
 */
export function pruneSelectedStopIds(
  prev: ReadonlySet<string>,
  index: StopSelectionIndex,
): Set<string> {
  if (prev.size === 0) return prev as Set<string>;

  const next = new Set<string>();
  let scope: number | null = null;
  let dropped = false;

  for (const stopId of prev) {
    const entry = index.get(stopId);
    if (!entry) {
      dropped = true;
      continue;
    }
    // 範囲が割れていたら、残った件数に関わらず捨てる
    if (scope == null) scope = entry.shiftTruckId;
    else if (scope !== entry.shiftTruckId) return new Set();
    next.add(stopId);
  }

  return dropped ? next : (prev as Set<string>);
}

/**
 * 選択した停車地が動かす legRun。
 *
 * Why uniq: 同じ集約カードを指す停車地行が複数ありうる（再訪）ため、地点を別々に選んでも
 * legRun は重複する。積地カードは同じ積地への再訪をマージするので、1 地点で複数案件が動く
 * （バーの「地点 N 件」と動く案件数は一致しない）。
 */
export function collectSelectedLegRunIds(
  selected: ReadonlySet<string>,
  index: StopSelectionIndex,
): number[] {
  const ids = new Set<number>();
  selected.forEach((stopId) => {
    index.get(stopId)?.legRunIds.forEach((id) => ids.add(id));
  });
  return [...ids];
}

/**
 * 一括割付の移動元。選択が空・範囲が割れている・index から消えているときは null。
 *
 * prune が同じ不変条件を守っているが、実行直前にも確かめる（fail-closed）。
 */
export function resolveStopSelectionSource(
  selected: ReadonlySet<string>,
  index: StopSelectionIndex,
): StopSelectionSource | null {
  let source: StopSelectionSource | null = null;
  for (const stopId of selected) {
    const entry = index.get(stopId);
    if (!entry) continue;
    const { rowId, shiftTruckId, shiftId } = entry;
    if (source == null) source = { rowId, shiftTruckId, shiftId };
    else if (source.shiftTruckId !== shiftTruckId) return null;
  }
  return source;
}

/**
 * rowId → その行で選択できる stopId。index 由来なので、選択が変わっても参照は変わらない。
 *
 * 選択できない地点に aria-pressed を出さないために、選択状態とは別に配る。
 */
export function buildSelectableStopIdsByRowId(index: StopSelectionIndex): Map<string, Set<string>> {
  const byRowId = new Map<string, Set<string>>();
  index.forEach((entry, stopId) => {
    const ids = byRowId.get(entry.rowId);
    if (ids) ids.add(stopId);
    else byRowId.set(entry.rowId, new Set([stopId]));
  });
  return byRowId;
}

/**
 * rowId → 選択中の stopId。**選択のある行だけ**入れる。
 *
 * Why 空 Set を全行へ配らない: 行ごとに毎回新しい参照が渡り、
 * VehicleListGroup の memo（areVehicleListGroupPropsEqual）が全グループで破れる。
 */
export function buildSelectedStopIdsByRowId(
  selected: ReadonlySet<string>,
  index: StopSelectionIndex,
): Map<string, Set<string>> {
  const byRowId = new Map<string, Set<string>>();
  selected.forEach((stopId) => {
    const entry = index.get(stopId);
    if (!entry) return;
    const ids = byRowId.get(entry.rowId);
    if (ids) ids.add(stopId);
    else byRowId.set(entry.rowId, new Set([stopId]));
  });
  return byRowId;
}
